import type { ReactNode } from "react";
import styles from "./SectionHeading.module.css";

/**
 * A section heading, exactly as the design canvas draws one: the title, a 2px brass rule
 * directly under it, and an optional line of description below that.
 *
 * There is no eyebrow. The build carried one — a brass mono caption above every title — and
 * the canvas has none; it made each section open with two competing labels where the canvas
 * opens with one. Brass is spent on the rule instead, which is the same association (rank
 * marks, section rules, the sponsored band) carried by one mark rather than two.
 */
export function SectionHeading({
  title,
  description,
  id,
  level = 2,
  meta,
  aside,
  compact = false,
}: {
  title: string;
  description?: ReactNode | undefined;
  id?: string | undefined;
  level?: 1 | 2 | 3 | undefined;
  /**
   * A factual line beside the title, in the figure face — the board uses it for the display
   * date and the reset time. It states a fact about the thing the title names, which is why
   * it sits on the title's own baseline rather than under it.
   */
  meta?: ReactNode | undefined;
  /** Trailing content on the title line, pushed to the end. The board's explainer link. */
  aside?: ReactNode | undefined;
  /**
   * Drop the rule and tighten the block. The Top 10 uses it: the board is followed
   * immediately by the brass disclosure band, which is a stronger rule than this one, and
   * two brass rules stacked read as a mistake.
   */
  compact?: boolean | undefined;
}) {
  const Title = level === 1 ? "h1" : level === 2 ? "h2" : "h3";
  return (
    <div className={`${styles.heading} ${compact ? styles.compact : ""}`.trim()}>
      <div className={styles.titleRow}>
        <Title id={id} className={styles.title}>
          {title}
        </Title>
        {meta ? <p className={`${styles.meta} figure`}>{meta}</p> : null}
        {aside ? <div className={styles.aside}>{aside}</div> : null}
      </div>
      {description ? <p className={styles.description}>{description}</p> : null}
    </div>
  );
}
